import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  KeyRound,
  Scale,
  RefreshCw,
  Clock as ClockIcon,
  CheckCircle,
  Home,
  Monitor,
  Link as LinkIcon,
  Timer,
  ChevronDown,
  ChevronRight,
  Undo2,
  Search,
  X,
} from 'lucide-react';
import { CorrectionButton } from '../components/CorrectionButton';
import { withdrawCorrection } from '../api/client';
import { CorrectionModal } from '../components/CorrectionModal';
// recharts nicht mehr benötigt (Donut entfernt)
import { WsaWizard } from '../components/WsaWizard';
import { FlWizard } from '../components/FlWizard';
import { DisruptionBox } from '../components/DisruptionBox';
import { ModuleInlineBadge } from '../components/ModuleInlineBadge';
import { Clock, ShiftDisplay, useCurrentShift } from '../components/Clock';
import { TimeoutWarning } from '../components/TimeoutWarning';
import { useAuth } from '../App';
import { AppFooter } from '../components/AppFooter';
import {
  getStationStatus,
  extendUnlock,
  stationLogout,
  createWsaEntry,
  getWsaEntries,
  createProcessingEntry,
  getProcessingEntries,
  ApiException,
  StationStatusResponse,
  WsaEntryResponse,
  ProcessingEntryResponse,
  CorrectionTargetType,
  KorrekturHinweis,
} from '../api/client';
import { Tooltip } from '../components/Tooltip';
import FeedbackDrawer from '../components/feedback/FeedbackDrawer';

/**
 * Durchsucht die heutigen FL-Erfassungen (Meldung #20).
 *
 * Gesucht wird ueber alles, was in der Zeile sichtbar ist: Kunde (Name und
 * Kuerzel), Kundengruppe, Strecke, Substrecke, Waescheklasse und
 * Artikelgruppe. Ohne Suchbegriff bleibt die Liste unveraendert.
 *
 * Exportiert, damit die Suchregel testbar ist, ohne den Kiosk zu rendern.
 */
export function filterProcessingEntries<
  T extends {
    line?: { name?: string | null } | null;
    subLine?: { name?: string | null } | null;
    customer?: { name?: string | null; shortName?: string | null } | null;
    customerGroup?: { name?: string | null } | null;
    laundryClassName?: string | null;
    articleGroupName?: string | null;
  },
>(entries: T[], search: string): T[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return entries;

  return entries.filter((entry) =>
    [
      entry.customer?.name,
      entry.customer?.shortName,
      entry.customerGroup?.name,
      entry.line?.name,
      entry.subLine?.name,
      entry.laundryClassName,
      entry.articleGroupName,
    ]
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .some((v) => v.toLowerCase().includes(needle)),
  );
}

/**
 * Hinweis auf eine angenommene Korrektur.
 *
 * Die Zeile zeigt bereits den korrigierten Stand — ohne diesen Hinweis wirkte
 * das wie ein Eingabefehler ("ich hatte doch 15 eingegeben"). Deshalb steht
 * hier, was geändert wurde, und bei Gewichten der Wert davor.
 */
function KorrekturHinweisBadge({ korrektur }: { korrektur: KorrekturHinweis }) {
  const felder = korrektur.felder.join(', ');
  const text =
    korrektur.gewichtVorher !== null
      ? `Korrigiert (${felder}) — Gewicht vorher ${korrektur.gewichtVorher} kg`
      : `Korrigiert (${felder})`;

  return (
    <Tooltip content={text}>
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-akzent bg-akzent-tief rounded whitespace-nowrap">
        <Check className="w-3.5 h-3.5" />
        korrigiert
      </span>
    </Tooltip>
  );
}

/**
 * Station Screen (Kiosk)
 *
 * Enterprise Kiosk-tauglich:
 * - Große Scan-Area
 * - Klare Zustände
 * - Timeout-Anzeige (Countdown)
 * - Auto-Lock serverseitig
 */
export default function StationScreen() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // URL-basierter Erfassungstyp: /wsa -> WSA, /vorarbeiter oder /fl -> VORARBEITER
  const urlBasedType =
    location.pathname === '/wsa'
      ? 'WSA'
      : location.pathname === '/vorarbeiter' || location.pathname === '/fl'
        ? 'VORARBEITER'
        : null;

  const [station, setStation] = useState<StationStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Admin-User sind automatisch "angemeldet" - kein RFID nötig
  const isAdminMode = user && ['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(user.role);
  const isLoggedIn = station?.activeWorker || isAdminMode;

  // Keepalive-Handler: Verlaengert Worker-Unlock TTL
  const handleKeepalive = useCallback(async () => {
    if (!station?.activeWorker) return;
    try {
      await extendUnlock(station.activeWorker.userId);
    } catch {
      // Ignorieren - naechster Keepalive wird es erneut versuchen
    }
  }, [station?.activeWorker]);

  // Timeout-Handler: Worker abmelden und zurueck zum Login
  const handleInactivityTimeout = useCallback(async () => {
    // Explizites Logout - loescht Redis Key sofort
    try {
      await stationLogout();
    } catch {
      // Ignorieren - navigate trotzdem
    }

    // SPA-Navigation zu /login (kein Full Reload)
    navigate('/login');
  }, [navigate]);

  // WARNING_THRESHOLD: Modal erscheint nur in den letzten X Sekunden
  const WARNING_THRESHOLD = 10;
  // Abmelde-Zeit kommt aus der Backend-Einstellung (P6 Block E); 180 nur bis zur ersten Status-Antwort
  const INACTIVITY_TIMEOUT = station?.unlockTtlSeconds ?? 180;
  // KEEPALIVE_THROTTLE: Max 1 Keepalive pro 20 Sekunden (throttle, nicht debounce)
  const KEEPALIVE_THROTTLE_MS = 20000;

  // Modal zeigen wenn countdown in Warnphase (basierend auf Redis TTL)
  const showTimeoutWarning =
    countdown > 0 && countdown <= WARNING_THRESHOLD && !!station?.activeWorker && !isAdminMode;

  // Throttle-Ref für Keepalive (leading=true: erste Aktivität sofort, dann gedrosselt)
  const lastKeepaliveRef = useRef<number>(0);
  const isFirstActivityRef = useRef<boolean>(true);
  // Logout-Deterministik: verhindert Doppel-Ausführung bei Race Conditions
  const didTimeoutRef = useRef<boolean>(false);

  // Activity Handler: Optimistic Reset + Throttled Keepalive
  useEffect(() => {
    if (!station?.activeWorker || isAdminMode) return;

    // Reset flags when worker changes (neuer Login)
    isFirstActivityRef.current = true;
    didTimeoutRef.current = false;

    const handleActivity = () => {
      // 1) OPTIMISTIC RESET: Countdown sofort auf 180s setzen (UX)
      // Micro-Opt: Nur resetten wenn sinnvoll (vermeidet Rerender-Spam bei wheel/scroll)
      setCountdown((prev) => (prev < INACTIVITY_TIMEOUT - 5 ? INACTIVITY_TIMEOUT : prev));

      // 2) THROTTLED KEEPALIVE (leading=true):
      //    - Erste Aktivität nach Login/Idle: sofort senden
      //    - Danach: max alle KEEPALIVE_THROTTLE_MS
      const now = Date.now();
      const timeSinceLastKeepalive = now - lastKeepaliveRef.current;

      if (isFirstActivityRef.current || timeSinceLastKeepalive >= KEEPALIVE_THROTTLE_MS) {
        isFirstActivityRef.current = false;
        lastKeepaliveRef.current = now;
        handleKeepalive();
      }
    };

    // Robuste Event-Liste: pointerdown (inkl. touch), keydown, scroll, wheel
    // scroll/wheel mit capture: fängt auch Events in Child-Elementen
    const addActivityListeners = () => {
      window.addEventListener('pointerdown', handleActivity, { passive: true });
      window.addEventListener('keydown', handleActivity, { passive: true });
      document.addEventListener('scroll', handleActivity, { passive: true, capture: true });
      window.addEventListener('wheel', handleActivity, { passive: true });
    };

    const removeActivityListeners = () => {
      window.removeEventListener('pointerdown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      document.removeEventListener('scroll', handleActivity, true); // capture=true
      window.removeEventListener('wheel', handleActivity);
    };

    addActivityListeners();
    return removeActivityListeners;
  }, [station?.activeWorker, isAdminMode, handleKeepalive, INACTIVITY_TIMEOUT]);

  // Dismiss Warning = Keepalive ausloesen + Optimistic Reset
  const dismissWarning = useCallback(() => {
    setCountdown(INACTIVITY_TIMEOUT); // Optimistic Reset
    handleKeepalive();
    lastKeepaliveRef.current = Date.now();
    isFirstActivityRef.current = false;
  }, [handleKeepalive, INACTIVITY_TIMEOUT]);

  // Effektiver Typ: URL hat Vorrang, sonst Station-Cookie
  const effectiveType = urlBasedType || station?.type || null;

  // Schichtbestimmung: Backend-basiert via useCurrentShift Hook (VS#16)
  const { data: currentShiftData, loading: currentShiftLoading } = useCurrentShift();
  const activeShift = {
    isActive: currentShiftData?.hasActiveShift ?? false,
    name: currentShiftData?.ruleName ?? 'Laden...',
    shiftType: currentShiftData?.shiftTypeCode ?? null,
  };

  // Karenz 18.08.: Nachzügler-Wahl direkt nach dem Schichtwechsel.
  // Standard ist immer die laufende Schicht; die Wahl gilt bis das Fenster
  // schließt (Server prüft die Frist bei jedem Speichern erneut).
  const nachzuegler = currentShiftData?.nachzuegler ?? null;
  const [buchtVorherigeSchicht, setBuchtVorherigeSchicht] = useState(false);
  useEffect(() => {
    if (!nachzuegler) setBuchtVorherigeSchicht(false);
  }, [nachzuegler]);

  // VS#11: WSA Erfassung State
  const [wsaEntries, setWsaEntries] = useState<WsaEntryResponse[]>([]);
  const [wsaLoading, setWsaLoading] = useState(false);
  const [wsaError, setWsaError] = useState('');
  const [wsaSuccess, setWsaSuccess] = useState('');
  const [weightInput, setWeightInput] = useState('');
  const [machineId, setMachineId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [programId, setProgramId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const weightInputRef = useRef<HTMLInputElement>(null);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [expandedParticipants, setExpandedParticipants] = useState<Set<string>>(new Set());

  // VS#12: FL/Processing Erfassung State
  const [flEntries, setFlEntries] = useState<ProcessingEntryResponse[]>([]);
  // Meldung #20: "Hab ich den Kunden xy schon erfasst?" - Suche ueber die
  // heutigen Erfassungen (Kunde, Gruppe, Strecke, Substrecke, Klasse, Gruppe).
  const [flSearch, setFlSearch] = useState('');
  const filteredFlEntries = filterProcessingEntries(flEntries, flSearch);
  const [flLoading, setFlLoading] = useState(false);
  const [flError, setFlError] = useState('');
  const [flSuccess, setFlSuccess] = useState('');
  const [flWeightInput, setFlWeightInput] = useState('');
  const [lineId, setLineId] = useState('');
  const [flCustomerId, setFlCustomerId] = useState('');
  const [flSubmitting, setFlSubmitting] = useState(false);
  const flWeightInputRef = useRef<HTMLInputElement>(null);

  // Correction Modal State (vereinfacht - Karten-Ansicht)
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState<{
    type: CorrectionTargetType;
    entry: WsaEntryResponse | ProcessingEntryResponse;
  } | null>(null);
  // Track welche Entries schon gemeldet wurden (optimistic UI + Server-State Merge)
  // V2 Hardening: reportedEntries wird bei loadEntries mit Server-State synchronisiert
  const [reportedEntries, setReportedEntries] = useState<Set<string>>(new Set());

  // Donut-Chart Farben: Primary Blue -> abgestuft -> Grau fuer "Andere"
  const DONUT_COLORS = ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#9ca3af'];
  const machineDonutData = useMemo(() => {
    if (wsaEntries.length === 0) return [];

    // Aggregiere kg + Anzahl pro Maschine
    const machineStats: Record<string, { name: string; kg: number; count: number }> = {};
    for (const entry of wsaEntries) {
      const machineName = entry.machine?.name || 'Unbekannt';
      if (!machineStats[machineName]) {
        machineStats[machineName] = { name: machineName, kg: 0, count: 0 };
      }
      machineStats[machineName].kg += entry.weightKg;
      machineStats[machineName].count += 1;
    }

    // Sortiere nach kg DESC, zeige alle (keine "Andere"-Kategorie)
    return Object.values(machineStats)
      .sort((a, b) => b.kg - a.kg)
      .map((m) => ({ name: m.name, value: m.kg, count: m.count }));
  }, [wsaEntries]);

  // Gruppierte WSA-Einträge nach washRunId
  interface WashRun {
    runId: string;
    machine: { name: string };
    program: { number: string; name: string } | null;
    totalKg: number;
    entries: WsaEntryResponse[];
    firstRecordedAt: string;
  }
  const groupedWsaRuns = useMemo((): WashRun[] => {
    const runMap = new Map<string, WashRun>();

    for (const entry of wsaEntries) {
      // Entries ohne washRunId: eigene Gruppe (key = entry.id)
      const runId = entry.washRunId || `single:${entry.id}`;

      if (!runMap.has(runId)) {
        runMap.set(runId, {
          runId,
          machine: entry.machine,
          program: entry.program,
          totalKg: 0,
          entries: [],
          firstRecordedAt: entry.recordedAt,
        });
      }
      const run = runMap.get(runId)!;
      run.totalKg += entry.weightKg;
      run.entries.push(entry);
      // Früheste Zeit behalten
      if (entry.recordedAt < run.firstRecordedAt) {
        run.firstRecordedAt = entry.recordedAt;
      }
    }

    // Sortiere nach Zeit DESC (neueste zuerst)
    return Array.from(runMap.values()).sort(
      (a, b) => new Date(b.firstRecordedAt).getTime() - new Date(a.firstRecordedAt).getTime(),
    );
  }, [wsaEntries]);

  const toggleExpandRun = (runId: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  const toggleExpandParticipants = (entryId: string) => {
    setExpandedParticipants((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  // KPI-Daten fuer FL: kg + Anzahl pro Strecke (inkl. Sub-Strecke)
  const lineDonutData = useMemo(() => {
    if (flEntries.length === 0) return [];

    // Aggregiere kg + Anzahl pro Strecke (Hauptstrecke + Sub-Strecke kombiniert)
    const lineStats: Record<string, { name: string; kg: number; count: number }> = {};
    for (const entry of flEntries) {
      const lineName = entry.line?.name || 'Unbekannt';
      const subLineName = entry.subLine?.name;
      const fullName = subLineName ? `${lineName} · ${subLineName}` : lineName;

      if (!lineStats[fullName]) {
        lineStats[fullName] = { name: fullName, kg: 0, count: 0 };
      }
      lineStats[fullName].kg += entry.weightKg;
      lineStats[fullName].count += 1;
    }

    // Sortiere nach kg DESC, zeige alle (keine "Andere"-Kategorie)
    return Object.values(lineStats)
      .sort((a, b) => b.kg - a.kg)
      .map((m) => ({ name: m.name, value: m.kg, count: m.count }));
  }, [flEntries]);

  // Station Status laden
  const loadStatus = useCallback(async (isInitial = false) => {
    // Nur beim initialen Load den Loading-State setzen
    if (isInitial) setLoading(true);

    try {
      const data = await getStationStatus();

      // Nur updaten wenn sich relevante Daten geändert haben (verhindert Re-Render/Flackern)
      // WICHTIG: expiresIn NICHT vergleichen - aendert sich bei jedem Poll und wuerde Flackern verursachen
      setStation((prev) => {
        if (!prev) return data;
        const hasChanged =
          prev.id !== data.id ||
          prev.type !== data.type ||
          prev.activeWorker?.userId !== data.activeWorker?.userId;
        return hasChanged ? data : prev;
      });
      setError('');

      // Countdown nur updaten wenn Worker aktiv
      // MAX-LOGIK: Server-Wert nur übernehmen wenn HÖHER als lokal (nie nach unten korrigieren)
      // Das verhindert, dass optimistic reset durch Polling überschrieben wird
      if (data.activeWorker) {
        const serverExpiry = data.activeWorker.expiresIn;
        setCountdown((prev) => {
          // Nur nach OBEN korrigieren (Server hat mehr Zeit als wir dachten)
          return serverExpiry > prev ? serverExpiry : prev;
        });
      } else {
        setCountdown(0);
      }
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error);
      } else {
        setError('Verbindungsfehler');
      }
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  // Polling-Intervall basierend auf countdown: >30s alle 10s, <=30s alle 1s
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    loadStatus(true); // Initial load mit Loading-State

    const startPolling = () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      // Dynamisches Intervall: <=30s = 1s, >30s = 10s
      const interval = countdown <= 30 && countdown > 0 ? 1000 : 10000;
      pollingIntervalRef.current = setInterval(() => loadStatus(false), interval);
    };

    startPolling();
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [loadStatus, countdown <= 30]);

  // Visibility Change Handler: Bei Rückkehr in Vordergrund sofort Resync
  useEffect(() => {
    if (!station?.activeWorker || isAdminMode) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // App wieder im Vordergrund: sofort Server-Sync + Extend
        isFirstActivityRef.current = true; // Nächste Activity triggert sofort Keepalive
        handleKeepalive();
        loadStatus(false); // Server-TTL abfragen
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [station?.activeWorker, isAdminMode, handleKeepalive, loadStatus]);

  // Countdown Timer - Source of Truth fuer Auto-Logout
  useEffect(() => {
    if (countdown > 0) {
      countdownRef.current = setTimeout(() => {
        // Hardening: Math.max(0, c-1) verhindert negative Werte bei Race/Batching
        setCountdown((c) => Math.max(0, c - 1));
      }, 1000);
    } else if (countdown === 0 && station?.activeWorker && !isAdminMode) {
      // Logout-Deterministik: nur einmal ausführen (Tick + Polling + visibilitychange Race)
      if (didTimeoutRef.current) return;
      didTimeoutRef.current = true;

      // Countdown abgelaufen (Redis TTL = 0) -> Logout
      // Server hat Session bereits invalidiert, wir navigieren nur noch
      console.log('[Timeout] countdown=0, triggering logout (once)');
      handleInactivityTimeout();
    }

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [countdown, station?.activeWorker, isAdminMode, handleInactivityTimeout]);

  // RFID-Handler wurde nach Login.tsx verschoben (einziger RFID-Handler)
  // StationScreen zeigt nur noch Erfassungsmaske wenn Worker/Admin angemeldet

  // Unlock verlängern
  const handleExtend = async () => {
    if (!station?.activeWorker) return;

    try {
      const result = await extendUnlock(station.activeWorker.userId);
      setCountdown(result.expiresIn);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error);
      }
    }
  };

  // VS#11: WSA Einträge laden
  const loadWsaEntries = useCallback(async () => {
    // Admin ohne Station ODER Worker mit Station
    if ((!station && !isAdminMode) || effectiveType !== 'WSA') return;

    setWsaLoading(true);
    try {
      const data = await getWsaEntries(station?.id ?? null);
      setWsaEntries(data.entries);

      // V2 Hardening: Server-State ERSETZT lokalen State vollständig
      // reportedEntries war nur Überbrückung bis zum Refetch
      // Ab jetzt ist entry.hasOpenCorrection die einzige Wahrheit
      const serverReported = new Set(
        data.entries.filter((e) => e.hasOpenCorrection).map((e) => e.id),
      );
      setReportedEntries(serverReported);

      setWsaError('');
    } catch (err) {
      if (err instanceof ApiException) {
        setWsaError(err.data.error);
      } else {
        setWsaError('Fehler beim Laden der Einträge');
      }
    } finally {
      setWsaLoading(false);
    }
  }, [station, isAdminMode, effectiveType]);

  // VS#11: WSA Eintrag erstellen
  const handleWsaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!station || submitting) return;

    const weight = parseFloat(weightInput);
    if (isNaN(weight) || weight <= 0) {
      setWsaError('Gewicht muss größer als 0 sein');
      return;
    }

    if (!machineId) {
      setWsaError('Bitte Maschine auswählen');
      return;
    }

    if (!customerId) {
      setWsaError('Bitte Kunde auswählen');
      return;
    }

    if (!programId) {
      setWsaError('Bitte Programm auswählen');
      return;
    }

    setSubmitting(true);
    setWsaError('');
    setWsaSuccess('');

    try {
      const entry = await createWsaEntry(station.id, {
        machineId,
        customerId,
        programId,
        weightKg: weight,
      });

      // Erfolg anzeigen
      setWsaSuccess(`${weight} kg erfasst`);
      setWeightInput('');

      // Nach 2s Toast ausblenden
      setTimeout(() => setWsaSuccess(''), 2000);

      // Einträge neu laden
      await loadWsaEntries();

      // Unlock verlängern (Save = Aktivitaet)
      if (station.activeWorker) {
        const result = await extendUnlock(station.activeWorker.userId);
        setCountdown(result.expiresIn);
        lastKeepaliveRef.current = Date.now();
      }

      // Focus auf Gewicht-Input
      weightInputRef.current?.focus();
    } catch (err) {
      if (err instanceof ApiException) {
        setWsaError(err.data.error);
      } else {
        setWsaError('Fehler beim Speichern');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // VS#11: Einträge laden wenn Worker angemeldet
  useEffect(() => {
    if ((station?.activeWorker || isAdminMode) && effectiveType === 'WSA') {
      loadWsaEntries();
    }
  }, [station?.activeWorker, effectiveType, isAdminMode, loadWsaEntries]);

  // VS#12: FL Einträge laden
  // K2: Zurueckziehen des eigenen offenen Korrektur-Antrags direkt am Eintrag
  const [zieheZurueck, setZieheZurueck] = useState<string | null>(null);
  const zieheKorrekturZurueck = async (correctionId: string, targetType: CorrectionTargetType) => {
    if (!station?.id) return;
    setZieheZurueck(correctionId);
    try {
      await withdrawCorrection(station.id, correctionId);
      if (targetType === 'WSA_ENTRY') await loadWsaEntries();
      else await loadFlEntries();
    } catch {
      // Antrag ist evtl. schon in Bearbeitung — Liste neu laden zeigt den Stand
      if (targetType === 'WSA_ENTRY') await loadWsaEntries();
      else await loadFlEntries();
    } finally {
      setZieheZurueck(null);
    }
  };

  const loadFlEntries = useCallback(async () => {
    // Admin ohne Station ODER Worker mit Station
    if ((!station && !isAdminMode) || effectiveType !== 'VORARBEITER') return;

    setFlLoading(true);
    try {
      const data = await getProcessingEntries(station?.id ?? null);
      setFlEntries(data.entries);

      // V2 Hardening: Server-State fuer reportedEntries synchronisieren (wie WSA)
      const serverReported = new Set(
        data.entries.filter((e) => e.hasOpenCorrection).map((e) => e.id),
      );
      setReportedEntries((prev) => {
        const merged = new Set(prev);
        serverReported.forEach((id) => merged.add(id));
        return merged;
      });

      setFlError('');
    } catch (err) {
      if (err instanceof ApiException) {
        setFlError(err.data.error);
      } else {
        setFlError('Fehler beim Laden der Einträge');
      }
    } finally {
      setFlLoading(false);
    }
  }, [station, isAdminMode, effectiveType]);

  // VS#12: FL Eintrag erstellen
  const handleFlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!station || flSubmitting) return;

    const weight = parseFloat(flWeightInput);
    if (isNaN(weight) || weight <= 0) {
      setFlError('Gewicht muss größer als 0 sein');
      return;
    }

    if (!lineId) {
      setFlError('Bitte Strecke auswählen');
      return;
    }

    if (!flCustomerId) {
      setFlError('Bitte Kunde auswählen');
      return;
    }

    setFlSubmitting(true);
    setFlError('');
    setFlSuccess('');

    try {
      await createProcessingEntry(station.id, {
        lineId,
        customerId: flCustomerId,
        weightKg: weight,
      });

      // Erfolg anzeigen
      setFlSuccess(`${weight} kg erfasst`);
      setFlWeightInput('');

      // Nach 2s Toast ausblenden
      setTimeout(() => setFlSuccess(''), 2000);

      // Einträge neu laden
      await loadFlEntries();

      // Unlock verlängern (Save = Aktivitaet)
      if (station.activeWorker) {
        const result = await extendUnlock(station.activeWorker.userId);
        setCountdown(result.expiresIn);
        lastKeepaliveRef.current = Date.now();
      }

      // Focus auf Gewicht-Input
      flWeightInputRef.current?.focus();
    } catch (err) {
      if (err instanceof ApiException) {
        setFlError(err.data.error);
      } else {
        setFlError('Fehler beim Speichern');
      }
    } finally {
      setFlSubmitting(false);
    }
  };

  // VS#12: Einträge laden wenn Worker angemeldet
  useEffect(() => {
    if ((station?.activeWorker || isAdminMode) && effectiveType === 'VORARBEITER') {
      loadFlEntries();
    }
  }, [station?.activeWorker, effectiveType, isAdminMode, loadFlEntries]);

  // Correction: Fehler melden Handler (vereinfacht - Karten-Modal)
  const openCorrectionModal = (
    targetType: CorrectionTargetType,
    entry: WsaEntryResponse | ProcessingEntryResponse,
  ) => {
    setCorrectionTarget({ type: targetType, entry });
    setCorrectionModalOpen(true);
  };

  // Correction: Erfolg-Handler (optimistic Badge)
  const handleCorrectionSuccess = (targetId: string) => {
    setReportedEntries((prev) => new Set(prev).add(targetId));
    // K2a-Nachfix: Liste sofort nachladen — erst mit der Antrags-Id vom Server
    // erscheint am Chip auch der Zurueckziehen-Knopf (vorher erst beim naechsten
    // Neuladen; an der WSA als "fehlt dort" gemeldet).
    if (correctionTarget?.type === 'WSA_ENTRY') {
      void loadWsaEntries();
    } else {
      void loadFlEntries();
    }
  };

  if (loading) {
    return (
      <div className="kiosk-container">
        <div className="spinner" />
      </div>
    );
  }

  // returnTo für Pairing (aktuelle URL)
  const currentPathAndQuery = location.pathname + location.search;
  const pairWithReturnTo = `/pair?returnTo=${encodeURIComponent(currentPathAndQuery)}`;

  // Ist Admin/Superadmin/Technician? (Betreiberrolle)
  const isAdminRole = user && ['ADMIN', 'SUPERADMIN', 'SYSTEM', 'TECHNICIAN'].includes(user.role);

  // KRITISCH: Station-only ohne activeWorker -> Redirect zu /login
  // Login.tsx ist der EINZIGE Chip-Scan-Screen (verhindert doppelte Handler)
  if (station && !station.activeWorker && !isAdminMode) {
    // Station gekoppelt, aber kein Worker angemeldet und kein Admin
    // -> Redirect zu /login für Chip-Scan (Login.tsx hat den einzigen RFID-Handler)
    window.location.href = '/login';
    return (
      <div className="kiosk-container">
        <div className="spinner" />
        <p className="text-tinte-3 mt-4">Weiterleitung zur Anmeldung...</p>
      </div>
    );
  }

  // Admin-Bypass: Admins duerfen ohne Station erfassen
  // Worker: Station zwingend erforderlich
  if (!station && !isAdminRole) {
    return (
      <div className="kiosk-container">
        <div className="kiosk-card text-center">
          <div className="w-16 h-16 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-stand" />
          </div>
          <h1 className="text-xl font-bold text-tinte">Station nicht gekoppelt</h1>
          <p className="text-tinte-3 mt-2 mb-4">
            {error || 'Diese Station muss zuerst mit dem System gekoppelt werden.'}
          </p>
          <p className="text-sm text-tinte-3 mb-4">Bitte wenden Sie sich an einen Administrator.</p>
          <a
            href="/login"
            className="inline-block px-4 py-2 bg-akzent text-akzent-satz rounded-lg hover:bg-akzent transition-colors"
          >
            Zur Anmeldung
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grund flex flex-col pb-12">
      {/* Header - Legacy Style */}
      <header className="bg-flaeche shadow">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Home-Button: Admin -> Dashboard, Worker/Station -> ausblenden (kein Login-Redirect) */}
            {isAdminMode && (
              <a
                href="/dashboard"
                className="p-2 border border-linie rounded-lg hover:bg-flaeche-2 transition-colors"
                title="Zum Dashboard"
              >
                <Home className="w-4 h-4 text-tinte-2" />
              </a>
            )}
            <h1 className="text-xl font-bold text-tinte">
              {effectiveType === 'WSA'
                ? 'WSA'
                : effectiveType === 'VORARBEITER'
                  ? 'Vorarbeiter'
                  : station?.name || 'Admin-Modus'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Uhrzeit - isolierte Komponente (verhindert Re-Render der Page) */}
            <Clock showDate={true} />
            <button
              onClick={() => loadStatus(false)}
              className="p-2 border border-linie rounded-lg hover:bg-flaeche-2 transition-colors"
              title="Aktualisieren"
            >
              <RefreshCw className="w-4 h-4 text-tinte-2" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4">
        {/* isLoggedIn ist immer true hier, da Redirect bei station-only ohne Worker greift */}
        {isLoggedIn && (
          // Worker oder Admin ist angemeldet
          <div className="max-w-4xl mx-auto">
            {/* Kompakter Status-Banner (Legacy-Style: minimal, nicht ablenkend) */}
            <div className="mb-4 px-4 py-2 bg-flaeche-2 border border-linie rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-lauf" />
                <span className="text-sm font-medium text-lauf">
                  {isAdminMode ? user?.displayName : station?.activeWorker?.displayName}
                </span>
                <span className="text-xs text-lauf">{isAdminMode ? '(Admin)' : ''}</span>
              </div>
              {/* Session-Status mit Countdown - nur fuer RFID-Worker */}
              {!isAdminMode && station?.activeWorker && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-lauf rounded-full animate-pulse" />
                    <span className="text-xs text-lauf">Aktiv</span>
                  </div>
                  {/* Auto-Logout Countdown Progress (3 Min = 180s) */}
                  {countdown > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-linie rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-1000 ease-linear ${
                            countdown <= 10 ? 'bg-stoer' : countdown <= 30 ? 'bg-stand' : 'bg-lauf'
                          }`}
                          style={{ width: `${Math.min(100, (countdown / 180) * 100)}%` }}
                        />
                      </div>
                      <div
                        className={`flex items-center gap-1 text-xs ${
                          countdown <= 10
                            ? 'text-stoer font-medium'
                            : countdown <= 30
                              ? 'text-stand'
                              : 'text-tinte-3'
                        }`}
                      >
                        <Timer className="w-3 h-3" />
                        <span>
                          Abmeldung in {Math.floor(countdown / 60)}:
                          {(countdown % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Störungs-Box - oben, nicht klappbar */}
            {(effectiveType === 'WSA' || effectiveType === 'VORARBEITER') && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-stand" />
                  <span className="text-sm font-medium text-tinte-2">Maschinenstörungen</span>
                  <ModuleInlineBadge moduleKey="disruptions" />
                </div>
                <DisruptionBox
                  stationId={station?.id}
                  stationType={effectiveType}
                  isAdminMode={!!isAdminMode}
                />
              </div>
            )}

            {/* VS#11: WSA Erfassungsmaske - Wizard (Hauptfokus) */}
            {effectiveType === 'WSA' && (
              <div>
                {/* Karenz 18.08.: Ein-Tipp-Wahl für Nachzügler direkt nach dem Schichtwechsel */}
                {nachzuegler && (
                  <div className="mb-4 px-3 py-3 bg-akzent-tief border border-akzent-tief rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-akzent mb-2">
                      <ClockIcon className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Schichtwechsel um {nachzuegler.endTime} Uhr — wohin gehört die Erfassung?
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setBuchtVorherigeSchicht(false)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          !buchtVorherigeSchicht
                            ? 'bg-akzent border-akzent text-akzent-satz'
                            : 'bg-flaeche border-akzent-tief text-akzent'
                        }`}
                      >
                        {currentShiftData?.shiftTypeName ?? 'Aktuelle Schicht'} (jetzt)
                      </button>
                      <button
                        type="button"
                        onClick={() => setBuchtVorherigeSchicht(true)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          buchtVorherigeSchicht
                            ? 'bg-akzent border-akzent text-akzent-satz'
                            : 'bg-flaeche border-akzent-tief text-akzent'
                        }`}
                      >
                        Noch {nachzuegler.shiftTypeName} (bis {nachzuegler.endTime})
                      </button>
                    </div>
                  </div>
                )}
                {!nachzuegler &&
                  currentShiftData?.graceWindowType === 'START' &&
                  currentShiftData.startTime && (
                    <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-akzent-tief border border-akzent-tief rounded-lg text-sm text-akzent">
                      <ClockIcon className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Die Schicht beginnt um {currentShiftData.startTime} Uhr — Erfassungen zählen
                        schon dazu.
                      </span>
                    </div>
                  )}
                <WsaWizard
                  stationId={station?.id ?? null}
                  isAdminMode={!!isAdminMode}
                  vorherigeSchicht={buchtVorherigeSchicht}
                  onSuccess={loadWsaEntries}
                  onExtendUnlock={() => {
                    if (station?.activeWorker) {
                      extendUnlock(station.activeWorker.userId);
                    }
                  }}
                />

                {/* Gap 24: Schicht-Summe - Gesamtgewicht dominant oben */}
                {wsaEntries.length > 0 && (
                  <div className="mt-6 bg-flaeche-2 border border-linie rounded-lg p-4">
                    {/* Titel */}
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="w-4 h-4 text-tinte-3" />
                      <span className="text-sm text-tinte-2">Schicht-Summe</span>
                    </div>
                    {/* Gesamtgewicht - dominant */}
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-tinte">
                        {wsaEntries
                          .reduce((sum, e) => sum + e.weightKg, 0)
                          .toLocaleString('de-DE', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}{' '}
                        kg
                      </div>
                      <div className="text-sm text-tinte-3">{wsaEntries.length} Erfassungen</div>
                    </div>
                    {/* Aufschlüsselung pro WSA */}
                    {machineDonutData.length > 0 &&
                      (() => {
                        const maxKg = machineDonutData[0]?.value || 1;
                        return (
                          <div className="space-y-2 border-t border-linie pt-3">
                            {machineDonutData.map((d) => (
                              <div key={d.name}>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-tinte-2">{d.name}</span>
                                  <span className="text-tinte font-medium">
                                    {d.value.toLocaleString('de-DE', {
                                      minimumFractionDigits: 1,
                                      maximumFractionDigits: 1,
                                    })}{' '}
                                    kg
                                  </span>
                                </div>
                                <div className="h-1 bg-flaeche-2 rounded mt-1">
                                  <div
                                    className="h-full rounded bg-akzent"
                                    style={{ width: `${(d.value / maxKg) * 100}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                  </div>
                )}

                {/* Gruppierte Waschgang-Liste */}
                <div className="mt-4 bg-flaeche rounded-lg border p-4">
                  <h3 className="text-base font-semibold text-tinte mb-3">
                    Heute erfasst ({wsaEntries.length} Einträge, {groupedWsaRuns.length} Waschgänge)
                  </h3>
                  {wsaLoading ? (
                    <div className="flex justify-center py-4">
                      <span className="spinner" />
                    </div>
                  ) : groupedWsaRuns.length === 0 ? (
                    <p className="text-sm text-tinte-3 text-center py-4">
                      Noch keine Erfassungen heute
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {groupedWsaRuns.slice(0, 15).map((run) => {
                        // Fuer Einzeleintraege: SYSTEM_BUCKET Badge + Teilnehmer direkt anzeigen
                        const singleEntry = run.entries.length === 1 ? run.entries[0] : null;
                        const isSingleSystemBucket =
                          singleEntry?.customerGroup?.type === 'SYSTEM_BUCKET';
                        const singleHasParticipants = (singleEntry?.participants?.length ?? 0) > 0;
                        const singleParticipantsExpanded = singleEntry
                          ? expandedParticipants.has(singleEntry.id)
                          : false;

                        return (
                          <div
                            key={run.runId}
                            className="rounded border border-linie overflow-hidden"
                          >
                            {/* Parent Row (Waschgang) */}
                            <div
                              onClick={() => run.entries.length > 1 && toggleExpandRun(run.runId)}
                              className={`p-3 bg-flaeche-2 flex justify-between items-center ${run.entries.length > 1 ? 'cursor-pointer hover:bg-grund' : ''}`}
                            >
                              <div className="flex items-center gap-2">
                                {run.entries.length > 1 ? (
                                  expandedRuns.has(run.runId) ? (
                                    <ChevronDown className="w-4 h-4 text-tinte-3" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-tinte-3" />
                                  )
                                ) : (
                                  <div className="w-4" />
                                )}
                                <div className="text-sm">
                                  <span className="font-medium">{run.machine.name}</span>
                                  <span className="text-tinte-3 mx-1">·</span>
                                  <span className="text-tinte-2">
                                    Prog. {run.program?.number || '-'}
                                  </span>
                                  {run.entries.length > 1 && (
                                    <span className="ml-2 text-xs bg-akzent-tief text-akzent px-1.5 py-0.5 rounded">
                                      {run.entries.length} Kunden
                                    </span>
                                  )}
                                  {/* Einzeleintrag: Kundenname + SYSTEM_BUCKET Badge */}
                                  {singleEntry && (
                                    <>
                                      <span className="text-tinte-3 mx-1">·</span>
                                      <span className="text-tinte-2">
                                        {/* Bei SYSTEM_BUCKET: nur Kundenname, Badge zeigt Sammelposten separat */}
                                        {isSingleSystemBucket
                                          ? singleEntry.customer?.shortName ||
                                            singleEntry.customer?.name ||
                                            'Kunde'
                                          : singleEntry.customer?.shortName ||
                                            singleEntry.customer?.name ||
                                            singleEntry.customerGroup?.name ||
                                            'Unbekannt'}
                                      </span>
                                      {isSingleSystemBucket && (
                                        <span className="ml-2 text-xs bg-flaeche-2 text-stoer px-1.5 py-0.5 rounded">
                                          {singleEntry.customerGroup?.name}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              {/* Rechte Gruppe: Zeit + kg + Action */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-tinte-3">
                                  {new Date(run.firstRecordedAt).toLocaleTimeString('de-DE', {
                                    timeZone: 'Europe/Berlin',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                <span className="px-2 py-1 bg-grund text-tinte-2 text-sm font-medium rounded">
                                  {run.totalKg.toFixed(1)} kg
                                </span>
                                {singleEntry?.correction && (
                                  <KorrekturHinweisBadge korrektur={singleEntry.correction} />
                                )}
                                {singleEntry &&
                                  (singleEntry.hasOpenCorrection ||
                                  reportedEntries.has(singleEntry.id) ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-stand bg-flaeche-2 rounded">
                                      <Undo2 className="w-3.5 h-3.5" />
                                      beantragt
                                      {station?.id && singleEntry.openCorrectionId && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            void zieheKorrekturZurueck(
                                              singleEntry.openCorrectionId!,
                                              'WSA_ENTRY',
                                            )
                                          }
                                          disabled={zieheZurueck === singleEntry.openCorrectionId}
                                          className="ml-1 underline text-stand disabled:opacity-50"
                                        >
                                          {zieheZurueck === singleEntry.openCorrectionId
                                            ? '…'
                                            : 'Zurückziehen'}
                                        </button>
                                      )}
                                    </span>
                                  ) : (
                                    <CorrectionButton
                                      onClick={() => openCorrectionModal('WSA_ENTRY', singleEntry)}
                                    />
                                  ))}
                              </div>
                            </div>

                            {/* Einzeleintrag SYSTEM_BUCKET: Teilnehmer-Bereich direkt anzeigen */}
                            {isSingleSystemBucket && singleEntry && (
                              <div className="px-3 py-2 bg-flaeche border-t border-linie">
                                {singleHasParticipants ? (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpandParticipants(singleEntry.id);
                                      }}
                                      className="flex items-center gap-1 text-sm text-tinte-3 hover:text-tinte-2"
                                    >
                                      {singleParticipantsExpanded ? (
                                        <ChevronDown className="w-4 h-4" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4" />
                                      )}
                                      <span>{singleEntry.participants?.length} Teilnehmer</span>
                                    </button>
                                    {singleParticipantsExpanded && (
                                      <div className="mt-2 ml-5 space-y-1">
                                        {singleEntry.participants?.map((p) => (
                                          <div key={p.id} className="text-sm text-tinte-2">
                                            {p.customer.shortName || p.customer.name}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2 text-sm text-stand">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>Keine Teilnehmer erfasst</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Child Rows (Kunden) - nur wenn expanded */}
                            {expandedRuns.has(run.runId) &&
                              run.entries.map((entry) => {
                                const isSystemBucket =
                                  entry.customerGroup?.type === 'SYSTEM_BUCKET';
                                const hasParticipants = (entry.participants?.length ?? 0) > 0;
                                const participantsExpanded = expandedParticipants.has(entry.id);

                                return (
                                  <div key={entry.id} className="bg-flaeche border-t border-linie">
                                    <div className="px-3 py-2 pl-10 text-sm flex justify-between items-center">
                                      <div className="flex items-center gap-2">
                                        <span className="text-tinte-2">
                                          {/* Bei SYSTEM_BUCKET: nur Kundenname, Badge zeigt Sammelposten separat */}
                                          {isSystemBucket
                                            ? entry.customer?.shortName ||
                                              entry.customer?.name ||
                                              'Kunde'
                                            : entry.customer?.shortName ||
                                              entry.customer?.name ||
                                              entry.customerGroup?.name ||
                                              'Unbekannt'}
                                        </span>
                                        {/* Badge fuer SYSTEM_BUCKET mit konkretem Namen */}
                                        {isSystemBucket && (
                                          <span className="text-xs bg-flaeche-2 text-stoer px-1.5 py-0.5 rounded">
                                            {entry.customerGroup?.name}
                                          </span>
                                        )}
                                      </div>
                                      {/* Rechte Gruppe: kg + Action */}
                                      <div className="flex items-center gap-2">
                                        <span className="px-2 py-0.5 bg-grund text-tinte-2 text-sm font-medium rounded">
                                          {entry.weightKg} kg
                                        </span>
                                        {entry.correction && (
                                          <KorrekturHinweisBadge korrektur={entry.correction} />
                                        )}
                                        {entry.hasOpenCorrection ||
                                        reportedEntries.has(entry.id) ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-stand bg-flaeche-2 rounded">
                                            <Undo2 className="w-3.5 h-3.5" />
                                            beantragt
                                            {station?.id && entry.openCorrectionId && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  void zieheKorrekturZurueck(
                                                    entry.openCorrectionId!,
                                                    'WSA_ENTRY',
                                                  )
                                                }
                                                disabled={zieheZurueck === entry.openCorrectionId}
                                                className="ml-1 underline text-stand disabled:opacity-50"
                                              >
                                                {zieheZurueck === entry.openCorrectionId
                                                  ? '…'
                                                  : 'Zurückziehen'}
                                              </button>
                                            )}
                                          </span>
                                        ) : (
                                          <CorrectionButton
                                            onClick={() => openCorrectionModal('WSA_ENTRY', entry)}
                                          />
                                        )}
                                      </div>
                                    </div>

                                    {/* Teilnehmer-Bereich fuer SYSTEM_BUCKET */}
                                    {isSystemBucket && (
                                      <div className="px-3 py-1 pl-10 border-t border-linie">
                                        {hasParticipants ? (
                                          <>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpandParticipants(entry.id);
                                              }}
                                              className="flex items-center gap-1 text-xs text-tinte-3 hover:text-tinte-2"
                                            >
                                              {participantsExpanded ? (
                                                <ChevronDown className="w-3 h-3" />
                                              ) : (
                                                <ChevronRight className="w-3 h-3" />
                                              )}
                                              <span>{entry.participants?.length} Teilnehmer</span>
                                            </button>
                                            {participantsExpanded && (
                                              <div className="mt-1 ml-4 space-y-0.5">
                                                {entry.participants?.map((p) => (
                                                  <div key={p.id} className="text-xs text-tinte-3">
                                                    {p.customer.shortName || p.customer.name}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="flex items-center gap-1 text-xs text-stand">
                                            <AlertTriangle className="w-3 h-3" />
                                            <span>Keine Teilnehmer erfasst</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* VS#12: FL/VORARBEITER Erfassungsmaske - Wizard */}
            {effectiveType === 'VORARBEITER' && (
              <div>
                {/* Karenz 18.08.: Ein-Tipp-Wahl für Nachzügler direkt nach dem Schichtwechsel */}
                {nachzuegler && (
                  <div className="mb-4 px-3 py-3 bg-akzent-tief border border-akzent-tief rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-akzent mb-2">
                      <ClockIcon className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Schichtwechsel um {nachzuegler.endTime} Uhr — wohin gehört die Erfassung?
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setBuchtVorherigeSchicht(false)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          !buchtVorherigeSchicht
                            ? 'bg-akzent border-akzent text-akzent-satz'
                            : 'bg-flaeche border-akzent-tief text-akzent'
                        }`}
                      >
                        {currentShiftData?.shiftTypeName ?? 'Aktuelle Schicht'} (jetzt)
                      </button>
                      <button
                        type="button"
                        onClick={() => setBuchtVorherigeSchicht(true)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          buchtVorherigeSchicht
                            ? 'bg-akzent border-akzent text-akzent-satz'
                            : 'bg-flaeche border-akzent-tief text-akzent'
                        }`}
                      >
                        Noch {nachzuegler.shiftTypeName} (bis {nachzuegler.endTime})
                      </button>
                    </div>
                  </div>
                )}
                {!nachzuegler &&
                  currentShiftData?.graceWindowType === 'START' &&
                  currentShiftData.startTime && (
                    <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-akzent-tief border border-akzent-tief rounded-lg text-sm text-akzent">
                      <ClockIcon className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Die Schicht beginnt um {currentShiftData.startTime} Uhr — Erfassungen zählen
                        schon dazu.
                      </span>
                    </div>
                  )}
                <FlWizard
                  stationId={station?.id ?? null}
                  isAdminMode={!!isAdminMode}
                  vorherigeSchicht={buchtVorherigeSchicht}
                  onSuccess={loadFlEntries}
                  onExtendUnlock={() => {
                    if (station?.activeWorker) {
                      extendUnlock(station.activeWorker.userId);
                    }
                  }}
                />

                {/* Gap 24: Schicht-Summe - Gesamtgewicht dominant oben */}
                {flEntries.length > 0 && (
                  <div className="mt-6 bg-flaeche-2 border border-linie rounded-lg p-4">
                    {/* Titel */}
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="w-4 h-4 text-tinte-3" />
                      <span className="text-sm text-tinte-2">Schicht-Summe</span>
                    </div>
                    {/* Gesamtgewicht - dominant */}
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-tinte">
                        {flEntries
                          .reduce((sum, e) => sum + e.weightKg, 0)
                          .toLocaleString('de-DE', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}{' '}
                        kg
                      </div>
                      <div className="text-sm text-tinte-3">{flEntries.length} Erfassungen</div>
                    </div>
                    {/* Aufschlüsselung pro Strecke */}
                    {lineDonutData.length > 0 &&
                      (() => {
                        const maxKg = lineDonutData[0]?.value || 1;
                        return (
                          <div className="space-y-2 border-t border-linie pt-3">
                            {lineDonutData.map((d) => (
                              <div key={d.name}>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-tinte-2">{d.name}</span>
                                  <span className="text-tinte font-medium">
                                    {d.value.toLocaleString('de-DE', {
                                      minimumFractionDigits: 1,
                                      maximumFractionDigits: 1,
                                    })}{' '}
                                    kg
                                  </span>
                                </div>
                                <div className="h-1 bg-flaeche-2 rounded mt-1">
                                  <div
                                    className="h-full rounded bg-lauf"
                                    style={{ width: `${(d.value / maxKg) * 100}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                  </div>
                )}

                {/* Gap 25: Kompakte Einträge-Liste wie Legacy */}
                <div className="mt-4 bg-flaeche rounded-lg border p-4">
                  <h3 className="text-base font-semibold text-tinte mb-3">
                    Heute erfasst (
                    {flSearch.trim()
                      ? `${filteredFlEntries.length} von ${flEntries.length}`
                      : flEntries.length}
                    )
                  </h3>

                  {/* Meldung #20: Suche in den Erfassungen */}
                  {flEntries.length > 0 && (
                    <div className="relative mb-3">
                      <Search className="w-4 h-4 text-tinte-3 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={flSearch}
                        onChange={(e) => setFlSearch(e.target.value)}
                        placeholder="Kunde oder Strecke suchen..."
                        className="w-full pl-9 pr-9 py-2 border border-linie-2 rounded-lg text-sm focus:ring-2 focus:ring-akzent focus:border-akzent"
                      />
                      {flSearch && (
                        <button
                          onClick={() => setFlSearch('')}
                          aria-label="Suche zurücksetzen"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-grund"
                        >
                          <X className="w-4 h-4 text-tinte-3" />
                        </button>
                      )}
                    </div>
                  )}
                  {flLoading ? (
                    <div className="flex justify-center py-4">
                      <span className="spinner" />
                    </div>
                  ) : flEntries.length === 0 ? (
                    <p className="text-sm text-tinte-3 text-center py-4">
                      Noch keine Erfassungen heute
                    </p>
                  ) : filteredFlEntries.length === 0 ? (
                    <p className="text-sm text-tinte-3 text-center py-4">
                      Keine Erfassung passt zu „{flSearch}"
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {/* Meldung #20: alle Erfassungen der Schicht, nicht nur die ersten 10 */}
                      {filteredFlEntries.map((entry) => (
                        <div key={entry.id} className="p-3 rounded bg-flaeche-2">
                          <div className="flex justify-between items-start">
                            <div className="text-sm space-y-1">
                              <div>
                                <span className="font-medium">
                                  {entry.line.name}
                                  {entry.subLine && (
                                    <span className="text-tinte-3"> · {entry.subLine.name}</span>
                                  )}
                                </span>
                                {entry.laundryClassName && (
                                  <span className="ml-2 text-xs bg-akzent-tief text-akzent px-1.5 py-0.5 rounded">
                                    {entry.laundryClassName}
                                  </span>
                                )}
                                {entry.articleGroupName && (
                                  <span className="ml-1 text-xs bg-flaeche-2 text-lauf px-1.5 py-0.5 rounded">
                                    {entry.articleGroupName}
                                  </span>
                                )}
                                <span className="text-tinte-3 mx-2">→</span>
                                <span>
                                  {entry.customer?.shortName ||
                                    entry.customer?.name ||
                                    entry.customerGroup?.name ||
                                    'Unbekannt'}
                                </span>
                              </div>
                            </div>
                            {/* Rechte Gruppe: Zeit + kg + Action */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-tinte-3">
                                {new Date(entry.recordedAt).toLocaleTimeString('de-DE', {
                                  timeZone: 'Europe/Berlin',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              <span className="px-2 py-1 bg-grund text-tinte-2 text-sm font-medium rounded">
                                {entry.weightKg} kg
                              </span>
                              {entry.correction && (
                                <KorrekturHinweisBadge korrektur={entry.correction} />
                              )}
                              {entry.hasOpenCorrection || reportedEntries.has(entry.id) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-stand bg-flaeche-2 rounded whitespace-nowrap">
                                  <Undo2 className="w-3.5 h-3.5" />
                                  beantragt
                                  {station?.id && entry.openCorrectionId && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        void zieheKorrekturZurueck(
                                          entry.openCorrectionId!,
                                          'PROCESSING_ENTRY',
                                        )
                                      }
                                      disabled={zieheZurueck === entry.openCorrectionId}
                                      className="ml-1 underline text-stand disabled:opacity-50"
                                    >
                                      {zieheZurueck === entry.openCorrectionId
                                        ? '…'
                                        : 'Zurückziehen'}
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <CorrectionButton
                                  onClick={() => openCorrectionModal('PROCESSING_ENTRY', entry)}
                                />
                              )}
                            </div>
                          </div>
                          {/* Korrektur-Preview: Kompakte Zeile mit vorgeschlagenen Aenderungen */}
                          {entry.openCorrectionPreview && (
                            <div className="mt-1.5 px-2 py-1.5 bg-flaeche-2 border border-linie rounded text-xs text-stand flex items-start gap-1.5">
                              <Undo2 className="w-3 h-3 mt-0.5 shrink-0 text-stand" />
                              <span>
                                Vorgeschlagen:{' '}
                                {entry.openCorrectionPreview.items.map((item, i) => {
                                  const labels: Record<string, string> = {
                                    lineId: 'Strecke',
                                    subLineId: 'Substrecke',
                                    customerId: 'Kunde',
                                    customerGroupId: 'Kundengruppe',
                                    weightKg: 'Gewicht',
                                    laundryClassId: 'Waescheklasse',
                                    articleGroupId: 'Artikelgruppe',
                                    machineId: 'Maschine',
                                    programId: 'Programm',
                                  };
                                  const label = labels[item.field] || item.field;
                                  const isWeight = item.field === 'weightKg';
                                  return (
                                    <span key={item.field}>
                                      {i > 0 && <span className="text-stand"> · </span>}
                                      <span className="font-medium">{label}</span>
                                      {isWeight ? (
                                        <span> → {String(item.newValue)} kg</span>
                                      ) : (
                                        <span className="text-stand"> geaendert</span>
                                      )}
                                    </span>
                                  );
                                })}
                                {entry.openCorrectionPreview.workerComment && (
                                  <span className="text-stand ml-1">
                                    ({entry.openCorrectionPreview.workerComment})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fallback für andere Stationstypen (ADMIN) */}
            {effectiveType !== 'WSA' && effectiveType !== 'VORARBEITER' && (
              <div className="mt-6 p-4 bg-flaeche-2 rounded-lg">
                <p className="text-sm text-tinte-2 text-center">
                  {effectiveType}-Erfassung wird später implementiert
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer - Produktname, Station und laufende Version */}
      <AppFooter kontext={station?.name || effectiveType || 'Admin-Modus'} />

      {/* Correction Modal - Karten-Ansicht */}
      {correctionModalOpen && correctionTarget && (
        <CorrectionModal
          entry={correctionTarget.entry}
          targetType={correctionTarget.type}
          stationId={station?.id ?? ''}
          onClose={() => setCorrectionModalOpen(false)}
          onSuccess={handleCorrectionSuccess}
        />
      )}

      {/* Timeout-Warnung Modal - NUR wenn countdown <= WARNING_THRESHOLD (Redis TTL) */}
      <TimeoutWarning
        show={showTimeoutWarning}
        remainingSeconds={countdown}
        onDismiss={dismissWarning}
      />
      {isLoggedIn && <FeedbackDrawer />}
    </div>
  );
}
